/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Sidebar, tituloBase } from "../../../../shared";
import { Toast } from "primereact/toast";
import { Skeleton } from "primereact/skeleton";
import { Divider } from "primereact/divider";
import { Menu } from "primereact/menu";
import {
  accessAPI,
  downloadFile,
  formatFecha,
  Boton,
  Titulo,
  Formulario,
  CampoInformacion,
  TablaFiltrable
} from "../../../../shared";

const SEARCH_FIELDS_ALCANCES = ["_normaNumero", "criterio"];

export default function Laboratorio() {
  const [loader, setLoader] = useState(true);
  const [laboratorio, setLaboratorio] = useState();
  const [alcances, setAlcances] = useState([]);

  // Editar laboratorio
  const [sideBarEditar, setSideBarEditar] = useState(false);
  const [loaderEditar, setLoaderEditar] = useState(false);
  const [formEditarKey, setFormEditarKey] = useState(0);

  // Alcances
  const [sideBarNuevoAlcance, setSideBarNuevoAlcance] = useState(false);
  const [loaderNuevoAlcance, setLoaderNuevoAlcance] = useState(false);
  const [formNuevoAlcanceKey, setFormNuevoAlcanceKey] = useState(0);
  const [sideBarEditarAlcance, setSideBarEditarAlcance] = useState(false);
  const [loaderEditarAlcance, setLoaderEditarAlcance] = useState(false);
  const [formEditarAlcanceKey, setFormEditarAlcanceKey] = useState(0);
  const [selectedAlcance, setSelectedAlcance] = useState(null);
  const [sideBarDetallesAlcance, setSideBarDetallesAlcance] = useState(false);
  const [loaderDescarga, setLoaderDescarga] = useState(false);

  const toast = useRef(null);
  const menuRef = useRef(null);
  const { laboratorioId } = useParams();

  // ── Laboratorio ──

  const camposEditables = [
    { campo: "razonsocial", etiqueta: "Razón social", requerido: true },
    { campo: "nombrefantasia", etiqueta: "Nombre fantasía" },
    { campo: "rut", etiqueta: "RUT" },
    { campo: "direccion", etiqueta: "Dirección", requerido: true },
    { campo: "codigopostal", etiqueta: "Código postal", requerido: true },
    { campo: "ciudad", etiqueta: "Ciudad", requerido: true },
    { campo: "estado", etiqueta: "Estado", requerido: true },
    { campo: "pais", etiqueta: "País", requerido: true },
    { campo: "telefono", etiqueta: "Teléfono" },
    { campo: "email", etiqueta: "Email", tipo: 2 },
    { campo: "pagina", etiqueta: "Página" },
    { campo: "notas", etiqueta: "Notas", tipo: 11 },
    { campo: "esCalibracion", etiqueta: "Lab de calibración", tipo: 6 }
  ];

  function guardarEdicion(contenidoCampos) {
    setLoaderEditar(true);
    accessAPI(
      "PUT",
      `admin/laboratorio/${laboratorioId}`,
      contenidoCampos,
      () => {
        setLaboratorio((prev) => ({ ...prev, ...contenidoCampos }));
        setSideBarEditar(false);
        setLoaderEditar(false);
      },
      (respuesta) => {
        setLoaderEditar(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al guardar",
          sticky: true
        });
      }
    );
  }

  function getEditarFormConfig() {
    return {
      secciones: [
        {
          campos: camposEditables.map((c) => ({
            nombre: c.campo,
            tipoitemformularioid: c.tipo || 1,
            etiqueta: c.etiqueta,
            requerido: c.requerido || false,
            respuesta: laboratorio[c.campo] ?? ""
          }))
        }
      ],
      botones: [
        {
          etiqueta: "Guardar cambios",
          callback: guardarEdicion,
          validar: true
        }
      ]
    };
  }

  function cargarLaboratorio() {
    accessAPI(
      "GET",
      `admin/laboratorio/${laboratorioId}`,
      null,
      (respuesta) => {
        setLaboratorio(respuesta);
        setAlcances(respuesta.alcances);
        document.title = respuesta.razonsocial;
        setLoader(false);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar laboratorio",
          sticky: true
        });
        setLoader(false);
      }
    );
  }

  // ── Alcances ──

  function crearAlcance(camposFormulario, archivo) {
    setLoaderNuevoAlcance(true);
    accessAPI(
      "POST",
      `admin/laboratorio/alcance/${laboratorio.id}`,
      camposFormulario,
      (respuesta) => {
        setAlcances((prev) => [...prev, respuesta]);
        setSideBarNuevoAlcance(false);
        setLoaderNuevoAlcance(false);
      },
      (respuesta) => {
        setLoaderNuevoAlcance(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al agregar alcance",
          sticky: true
        });
      },
      archivo
    );
  }

  function getNuevoAlcanceFormConfig() {
    return {
      secciones: [
        {
          campos: [
            {
              nombre: "normaid",
              tipoitemformularioid: 5,
              etiqueta: "Norma",
              opciones: "admin/norma",
              requerido: true,
              sinOpcionInicial: true
            },
            {
              nombre: "criterio",
              tipoitemformularioid: 1,
              etiqueta: "Criterio de aceptación",
              requerido: true
            },
            {
              nombre: "fechavalidez",
              tipoitemformularioid: 7,
              etiqueta: "Fecha adquisición",
              requerido: true
            },
            {
              nombre: "archivoexistente",
              tipoitemformularioid: 5,
              etiqueta: "Archivo de evidencia existente",
              opciones: `admin/laboratorio/archivosevidencias/${laboratorio.id}`,
              sinOpcionInicial: true,
              requerido: false
            },
            {
              nombre: "evidencia",
              tipoitemformularioid: 10,
              etiqueta: "Archivo de evidencia nuevo",
              requerido: false
            },
            {
              nombre: "enevaluacion",
              tipoitemformularioid: 6,
              etiqueta: "En evaluación (permite utilizarlo en procesos)",
              requerido: false
            },
            {
              nombre: "normaequivalente",
              tipoitemformularioid: 1,
              etiqueta: "Norma equivalente",
              requerido: false
            },
            {
              nombre: "documentohomologacion",
              tipoitemformularioid: 1,
              etiqueta: "Documento de homologación",
              requerido: false
            },
            {
              nombre: "notas",
              tipoitemformularioid: 11,
              etiqueta: "Notas",
              requerido: false
            }
          ]
        }
      ],
      botones: [
        {
          etiqueta: "Agregar alcance",
          callback: crearAlcance,
          validar: true
        }
      ]
    };
  }

  function editarAlcance(camposFormulario, archivo) {
    setLoaderEditarAlcance(true);
    accessAPI(
      "PUT",
      `admin/laboratorio/alcance/${selectedAlcance.id}`,
      camposFormulario,
      (respuesta) => {
        setAlcances((prev) =>
          prev.map((a) => (a.id === respuesta.id ? respuesta : a))
        );
        setSideBarEditarAlcance(false);
        setLoaderEditarAlcance(false);
      },
      (respuesta) => {
        setLoaderEditarAlcance(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al editar alcance",
          sticky: true
        });
      },
      archivo
    );
  }

  function getEditarAlcanceFormConfig() {
    if (!selectedAlcance) return null;
    return {
      secciones: [
        {
          campos: [
            {
              nombre: "normaid",
              tipoitemformularioid: 5,
              etiqueta: "Norma",
              opciones: "admin/norma",
              requerido: true,
              sinOpcionInicial: true,
              respuesta: selectedAlcance.normaid
            },
            {
              nombre: "criterio",
              tipoitemformularioid: 1,
              etiqueta: "Criterio de aceptación",
              requerido: true,
              respuesta: selectedAlcance.criterio || ""
            },
            {
              nombre: "fechavalidez",
              tipoitemformularioid: 7,
              etiqueta: "Fecha adquisición",
              requerido: true,
              respuesta: selectedAlcance.fechavalidez
                ? new Date(selectedAlcance.fechavalidez)
                : null
            },
            {
              nombre: "archivo",
              tipoitemformularioid: 10,
              etiqueta: "Reemplazar archivo de evidencia",
              requerido: false
            },
            {
              nombre: "activo",
              tipoitemformularioid: 6,
              etiqueta: "Activo",
              requerido: false,
              respuesta: selectedAlcance.activo
            },
            {
              nombre: "enevaluacion",
              tipoitemformularioid: 6,
              etiqueta: "En evaluación",
              requerido: false,
              respuesta: selectedAlcance.enevaluacion
            },
            {
              nombre: "normaequivalente",
              tipoitemformularioid: 1,
              etiqueta: "Norma equivalente",
              requerido: false,
              respuesta: selectedAlcance.normaequivalente || ""
            },
            {
              nombre: "documentohomologacion",
              tipoitemformularioid: 1,
              etiqueta: "Documento de homologación",
              requerido: false,
              respuesta: selectedAlcance.documentohomologacion || ""
            },
            {
              nombre: "notas",
              tipoitemformularioid: 11,
              etiqueta: "Notas",
              requerido: false,
              respuesta: selectedAlcance.notas || ""
            }
          ]
        }
      ],
      botones: [
        {
          etiqueta: "Guardar cambios",
          callback: editarAlcance,
          validar: true
        }
      ]
    };
  }

  function descargarArchivoAlcance(alcance) {
    if (!alcance.archivo || loaderDescarga) return;
    setLoaderDescarga(true);
    downloadFile(
      `admin/archivo/${alcance.archivo.id}`,
      alcance.archivo.nombredescarga,
      () => setLoaderDescarga(false),
      (respuesta) => {
        setLoaderDescarga(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al descargar archivo",
          sticky: true
        });
      }
    );
  }

  function getMenuItems() {
    const items = [
      {
        label: "Ver detalles",
        icon: "pi pi-eye",
        command: () => setSideBarDetallesAlcance(true)
      },
      {
        label: "Editar",
        icon: "pi pi-pencil",
        command: () => {
          setFormEditarAlcanceKey((k) => k + 1);
          setSideBarEditarAlcance(true);
        }
      }
    ];
    if (selectedAlcance?.archivo) {
      items.push({
        label: loaderDescarga ? "Descargando..." : "Descargar archivo",
        icon: loaderDescarga ? "pi pi-spin pi-spinner" : "pi pi-download",
        command: () => descargarArchivoAlcance(selectedAlcance)
      });
    }
    return items;
  }

  useEffect(() => {
    cargarLaboratorio();
    return () => (document.title = tituloBase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const datosAlcances = (alcances || []).map((a) => ({
    ...a,
    _normaNumero: a.norma?.numero || ""
  }));

  return (
    <>
      <Toast ref={toast} position="top-center" />

      {/* Sidebar editar laboratorio */}
      <Sidebar
        visible={sideBarEditar}
        onHide={() => setSideBarEditar(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Editar laboratorio</Titulo>
        {laboratorio && (
          <Formulario
            key={formEditarKey}
            config={getEditarFormConfig()}
            loader={loaderEditar}
          />
        )}
      </Sidebar>

      {/* Sidebar nuevo alcance */}
      <Sidebar
        visible={sideBarNuevoAlcance}
        onHide={() => setSideBarNuevoAlcance(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Nuevo alcance</Titulo>
        {laboratorio && (
          <Formulario
            key={formNuevoAlcanceKey}
            config={getNuevoAlcanceFormConfig()}
            loader={loaderNuevoAlcance}
          />
        )}
      </Sidebar>

      {/* Sidebar editar alcance */}
      <Sidebar
        visible={sideBarEditarAlcance}
        onHide={() => setSideBarEditarAlcance(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Editar alcance</Titulo>
        {selectedAlcance && (
          <Formulario
            key={formEditarAlcanceKey}
            config={getEditarAlcanceFormConfig()}
            loader={loaderEditarAlcance}
          />
        )}
      </Sidebar>

      {/* Sidebar detalles alcance */}
      <Sidebar
        visible={sideBarDetallesAlcance}
        onHide={() => setSideBarDetallesAlcance(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        {selectedAlcance && (
          <>
            <Titulo>{selectedAlcance.norma?.numero}</Titulo>
            <div className="grid">
              <CampoInformacion nombreCampo="Norma">
                {selectedAlcance.norma?.numero}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Criterio de aceptación">
                {selectedAlcance.criterio}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Fecha adquisición">
                {formatFecha(selectedAlcance.fechavalidez)}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Activo">
                {selectedAlcance.activo ? "Sí" : "No"}
              </CampoInformacion>
              <CampoInformacion nombreCampo="En evaluación">
                {selectedAlcance.enevaluacion ? "Sí" : "No"}
              </CampoInformacion>
              {selectedAlcance.normaequivalente && (
                <CampoInformacion nombreCampo="Norma equivalente">
                  {selectedAlcance.normaequivalente}
                </CampoInformacion>
              )}
              {selectedAlcance.documentohomologacion && (
                <CampoInformacion nombreCampo="Documento de homologación">
                  {selectedAlcance.documentohomologacion}
                </CampoInformacion>
              )}
              {selectedAlcance.notas && (
                <CampoInformacion nombreCampo="Notas" anchoCompleto>
                  <span css={{ whiteSpace: "pre-line" }}>
                    {selectedAlcance.notas}
                  </span>
                </CampoInformacion>
              )}
            </div>
            {selectedAlcance.archivo && (
              <>
                <Divider />
                <Boton
                  loader={loaderDescarga}
                  callback={() => descargarArchivoAlcance(selectedAlcance)}
                >
                  <i
                    className="pi pi-download"
                    css={{ marginRight: "0.5em" }}
                  />
                  {selectedAlcance.archivo.nombredescarga}
                </Boton>
              </>
            )}
          </>
        )}
      </Sidebar>

      <Menu model={getMenuItems()} popup ref={menuRef} />

      {loader && (
        <div
          css={{
            marginTop: "2em",
            display: "flex",
            flexDirection: "column",
            gap: "1em"
          }}
        >
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <div css={{ display: "flex", alignItems: "center", gap: "1em" }}>
              <Skeleton width="2rem" height="2rem" shape="circle" />
              <Skeleton width="15rem" height="2rem" />
            </div>
          </div>
          <Card>
            <Skeleton
              width="6rem"
              height="1.5rem"
              css={{ marginBottom: "1.5em" }}
            />
            <div className="grid">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="col-12 md:col-4">
                  <Skeleton
                    width="50%"
                    height="0.8rem"
                    css={{ marginBottom: "0.5rem" }}
                  />
                  <Skeleton width="80%" height="1.2rem" />
                </div>
              ))}
            </div>
          </Card>
          <Card>
            <div
              css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
            >
              <Skeleton width="100%" height="3rem" />
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} width="100%" height="3.5rem" />
              ))}
            </div>
          </Card>
        </div>
      )}

      {!loader && laboratorio && (
        <div>
          <Titulo urlVolver="/proveedores/laboratorios">
            {laboratorio.razonsocial}
          </Titulo>

          <Card>
            <Titulo
              chico
              botones={
                <Boton
                  texto
                  negro
                  callback={() => {
                    setFormEditarKey((k) => k + 1);
                    setSideBarEditar(true);
                  }}
                >
                  <i className="pi pi-pencil" />
                </Boton>
              }
            >
              Detalles
            </Titulo>
            <div className="grid">
              <CampoInformacion nombreCampo="Razón social">
                {laboratorio.razonsocial}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Nombre fantasía">
                {laboratorio.nombrefantasia}
              </CampoInformacion>
              <CampoInformacion nombreCampo="RUT">
                {laboratorio.rut}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Dirección">
                {laboratorio.direccion}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Código postal">
                {laboratorio.codigopostal}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Ciudad">
                {laboratorio.ciudad}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Estado">
                {laboratorio.estado}
              </CampoInformacion>
              <CampoInformacion nombreCampo="País">
                {laboratorio.pais}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Teléfono">
                {laboratorio.telefono}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Email">
                {laboratorio.email}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Página">
                {laboratorio.pagina}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Lab de calibración">
                {laboratorio.esCalibracion ? "Sí" : "No"}
              </CampoInformacion>
            </div>
            {laboratorio.notas && (
              <>
                <Divider />
                <div className="grid">
                  <CampoInformacion nombreCampo="Notas" anchoCompleto>
                    <span css={{ whiteSpace: "pre-line" }}>
                      {laboratorio.notas}
                    </span>
                  </CampoInformacion>
                </div>
              </>
            )}
          </Card>

          <Card css={{ marginTop: "1em" }}>
            <Titulo
              chico
              botones={
                <Boton
                  texto
                  negro
                  callback={() => {
                    setFormNuevoAlcanceKey((k) => k + 1);
                    setSideBarNuevoAlcance(true);
                  }}
                >
                  <i className="pi pi-plus" />
                </Boton>
              }
            >
              Alcances
            </Titulo>
            <TablaFiltrable
              value={datosAlcances}
              searchFields={SEARCH_FIELDS_ALCANCES}
              anchoBusqueda="33%"
              nombreArchivo="alcances_laboratorio"
              emptyMessage="No hay alcances registrados"
              tableStyle={{ tableLayout: "fixed" }}
            >
              <Column
                header="Norma"
                body={(rowData) => rowData.norma?.numero}
              />
              <Column
                header="Norma equivalente"
                body={(rowData) => rowData.normaequivalente || "-"}
              />
              <Column header="Criterio de aceptación" field="criterio" />
              <Column
                header="Activo"
                body={(rowData) =>
                  rowData.activo ? (
                    <i className="pi pi-check" css={{ color: "var(--verde)" }} />
                  ) : null
                }
              />
              <Column
                header="Fecha adquisición"
                body={(rowData) => formatFecha(rowData.fechavalidez)}
              />
              <Column
                body={(rowData) => (
                  <Boton
                    texto
                    negro
                    chico
                    callback={(e) => {
                      setSelectedAlcance(rowData);
                      menuRef.current.toggle(e);
                    }}
                  >
                    <i className="pi pi-ellipsis-v" />
                  </Boton>
                )}
                style={{ width: "60px", textAlign: "center" }}
              />
            </TablaFiltrable>
          </Card>
        </div>
      )}
    </>
  );
}
